import { useBreakpointValue } from '@chakra-ui/media-query';
import {
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link as ChakraLink,
  Stack,
  Text,
} from '@chakra-ui/react';
import Link from 'next/link';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useSignUp } from 'src/app/auth/use-auth';
import { logger } from 'src/lib/logger';
import { PasswordInput } from '../components/password-input';
import { useEmailSmartCompose } from '../email';

export interface SignUpFormProps {
  setVerifyStatus: (status: boolean) => void;
  setVerifyToken: (token: string) => void;
  setUserInfo: (userInfo: {
    username: string;
    email: string;
    password: string;
  }) => void;
}

function SignUpForm({
  setVerifyStatus,
  setVerifyToken,
  setUserInfo,
}: SignUpFormProps) {
  const headingFontSize = useBreakpointValue({ base: 'xl', md: '2xl' });
  const methods = useForm();
  const { register, handleSubmit, errors } = methods;
  const { data: response, isLoading, mutateAsync: createUser } = useSignUp();

  const onSubmit = async (data) => {
    try {
      setUserInfo({
        username: data.username,
        email: data.email,
        password: data.password,
      });

      const createUserResponse = await createUser(data);

      if (createUserResponse.code === 1000) {
        setVerifyToken(createUserResponse.data.token);
        setVerifyStatus(true);
      }
    } catch (err) {
      logger.error(err);
    }
  };

  useEmailSmartCompose('email-input');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={[3, 2, 4]}>
        <Heading size={headingFontSize} mb={2}>
          Get Started
        </Heading>

        <FormControl id="email">
          <FormLabel mb={0} fontSize="sm">
            Email
          </FormLabel>
          <Input
            id="email-input"
            data-testid="email"
            name="email"
            type="email"
            style={{
              fontFeatureSettings: `'calt' 0`,
            }}
            autoComplete="email"
            placeholder="welcome@1tm.io"
            variant="flushed"
            focusBorderColor="black"
            isInvalid={errors?.email || response?.code === 1004}
            ref={register({
              required: true,
              // ref: https://ui.dev/validate-email-address-javascript/
              pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            })}
          />
          {errors?.email?.type === 'pattern' && (
            <Text color="red.500" fontSize="xs" data-testid="invalid-email">
              Invalid Email Address
            </Text>
          )}
          {response?.code === 1004 && (
            <Text color="red.500" fontSize="xs" data-testid="duplicate-email">
              Email that have already been used.
            </Text>
          )}
        </FormControl>

        <FormControl id="username">
          <FormLabel mb={0} fontSize="sm">
            Username
          </FormLabel>
          <Input
            data-testid="username"
            autoComplete="username"
            name="username"
            type="text"
            ref={register({ required: true, pattern: /[A-Za-z0-9_]{4,20}/ })}
            placeholder="Create a unique username"
            variant="flushed"
            isInvalid={errors?.username || response?.code === 1011}
            focusBorderColor="black"
          />
          {response?.code === 1011 && (
            <Text
              color="red.500"
              fontSize="xs"
              data-testid="duplicate-username"
            >
              Accounts that have already been used.
            </Text>
          )}
        </FormControl>

        <PasswordInput id="password" methods={methods} />

        <FormControl id="agree">
          <Stack direction="row">
            <Checkbox
              name="agree"
              ref={register({ required: true }) as any}
              isInvalid={errors?.agree?.type === 'required'}
            />{' '}
            <Text fontSize="xs">
              I agree to{' '}
              <Link href="/#terms" passHref>
                <ChakraLink>Terms and Conditions</ChakraLink>
              </Link>
            </Text>
          </Stack>
        </FormControl>

        <Stack spacing={3} alignItems={['center', 'flex-start']}>
          <Button
            rounded="ellipse"
            size="md"
            width="130px"
            type="submit"
            isDisabled={isLoading}
          >
            Register
          </Button>

          <Text color="gray.500" fontSize="sm">
            Already have an account?{' '}
            <Link href="/signin" passHref>
              <ChakraLink>Sign in</ChakraLink>
            </Link>
          </Text>
        </Stack>
      </Stack>
    </form>
  );
}

export default SignUpForm;
